import "./layerContent.css";

import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import { ChevronLeft } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import {
  sidebarStore,
  WebServiceLayerType,
} from "shared/common/stores/sidebarStore";
import { addLayers } from "shared/common/utils/layers/addLayers";
import AddWebServiceLayer from "./addLayerItems/AddWebServiceLayer";
import CreateGroup from "./addLayerItems/CreateGroup";
import CreateLayer from "./addLayerItems/CreateLayer";
import { DropLayer } from "./addLayerItems/DropLayer";
import EditWebServiceLayers from "./addLayerItems/EditWebServiceLayers";
import SelectWebServiceLayers from "./addLayerItems/SelectWebServiceLayers";
import {
  getLayersAddedByUser,
  saveLayersToLocalStorage,
} from "shared/common/utils/layers/localStorageLayers";
import { IUnsavedLayerChanges } from "../../../../../common/utils/layers/layerutils";
import { v4 as uuidv4 } from "uuid";

interface LayerContentList {
  id: string;
  label: string;
  layername: string;
  description: string;
  type: string;
  component?: JSX.Element;
  event?: string;
}

export default function AddLayerContent() {
  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const layerConfiguration = defaultStore((state) => state.layerConfiguration);
  const changeLayerConfiguration = defaultStore(
    (state) => state.changeLayerConfiguration
  );
  const changeLayersToAdd = defaultStore((state) => state.changeLayersToAdd);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);

  const changeExpandAddLayersList = sidebarStore(
    (state) => state.changeExpandAddLayersList
  );
  const activeAddLayersListItem = sidebarStore(
    (state) => state.activeAddLayersListItem
  );
  const changeActiveAddLayersListItem = sidebarStore(
    (state) => state.changeActiveAddLayersListItem
  );
  const saveButtonStateOnAddNewLayers = sidebarStore(
    (state) => state.saveButtonStateOnAddNewLayers
  );
  const changeSaveButtonStateOnAddNewLayers = sidebarStore(
    (state) => state.changeSaveButtonStateOnAddNewLayers
  );
  const newLayerGroupList = sidebarStore((state) => state.newLayerGroupList);
  const changeNewLayerGroupList = sidebarStore(
    (state) => state.changeNewLayerGroupList
  );
  const webServiceLayer = sidebarStore((state) => state.webServiceLayer);
  const changeWebServiceLayer = sidebarStore(
    (state) => state.changeWebServiceLayer
  );
  const webServiceLayerStep = sidebarStore(
    (state) => state.webServiceLayerStep
  );
  const changeWebServiceLayerStep = sidebarStore(
    (state) => state.changeWebServiceLayerStep
  );
  const layerGroups = sidebarStore((state) => state.layerGroups);
  const changeLayerGroups = sidebarStore((state) => state.changeLayerGroups);
  const changeWebServiceLayersList = sidebarStore(
    (state) => state.changeWebServiceLayersList
  );
  const changeMaximizeTable = sidebarStore(
    (state) => state.changeMaximizeTable
  );

  const { t } = useTranslation();

  const [api, setApi] = useState<
    undefined | mm.WFS20 | mm.WFSGeoserver | mm.WFSQGIS | mm.ArcgisFeatureServer
  >(undefined);

  const emptyLayerChanges: IUnsavedLayerChanges = {
    title: "",
    topic: "",
    geometrytype: mm.GEOMETRYTYPE.UNDEFINED,
    propertydef: {
      add: [],
      remove: [],
      rename: [],
      applied: [],
    },
    style: {
      stylename: "",
      rules: [],
    },
  };

  const [unsavedLayerChanges, setUnsavedLayerChanges] =
    useState<IUnsavedLayerChanges>(emptyLayerChanges);

  //open window for selected list item
  const handleLayer = (item: string) => {
    changeActiveAddLayersListItem(item);
  };

  // call events for different list items
  const handleEvent = (item: string) => {
    if (item === "download") {
      downloadLayerConfig();
    }
    // if (item === "reset") {
    //   resetLayers();
    //   changeExpandAddLayersList(false);
    // }
    if (item === "upload") {
      uploadLayers();
    }
  };

  //download all layers as a json
  const downloadLayerConfig = () => {
    const allLayersFromUser = getLayersAddedByUser();
    const allLayersList = viewer?.getScene().getLayerHandler().layers;

    allLayersFromUser.forEach((items) => {
      items.layers?.forEach((item) => {
        const exactLayer = allLayersList?.find(
          (layers) =>
            layers.layername === item.layername &&
            layers.topic === item.topic &&
            layers.geometrytype === Number(item.geometrytype)
        );
        if (
          exactLayer &&
          exactLayer instanceof mm.MemoryLayer &&
          exactLayer.data
        ) {
          (item as mm.IMemoryLayerOptions).data = exactLayer.data;
        }
      });
    });

    const configurations = [...layerConfiguration, ...allLayersFromUser];
    const source = JSON.stringify(configurations);

    const jsonDataStr =
      "data:text/json;charset=utf-8," + encodeURIComponent(source);

    const a = document.createElement("a");
    a.download = "geodataconfiguration.json";
    a.href = jsonDataStr;
    document.body.appendChild(a);
    a.click();
    a.remove();
    changeExpandAddLayersList(false);
    changeMaximizeTable(false);
  };

  // //reset layers
  // const resetLayers = () => {
  //   changeResetLayerConfiguration(true);
  // };

  //upload layers from file
  const uploadLayers = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.click();
    input.onchange = (event) => {
      handleUploadFile(event);
    };
  };

  // "Geodatenkonfiguration importieren" Button
  //if a file is uploaded change layer configuration
  const handleUploadFile = (event: any) => {
    const reader = new FileReader();
    reader.readAsText(event.target.files[0]);
    reader.onload = () => {
      const rawLog = reader.result as string;
      try {
        const configs = JSON.parse(rawLog);
        configs.forEach((source: any) => {
          source.addedFromUser = true;
          source.layers.forEach((layer: any) => {
            if (typeof layer.geometrytype === "string") {
              layer.geometrytype = mm.Geometry.toNumber(layer.geometrytype);
            }
            layer.addedFromUser = true;
            layer.id = layer.id || uuidv4();
          });
          if (source.sourceoptions) {
            // Mapping to support classic configs!
            source.source_options = JSON.parse(
              JSON.stringify(source.sourceoptions)
            );
            delete source.sourceoptions;
          }
        });
        changeLayersToAdd(configs);

        const allLayersAddedFromUser = getLayersAddedByUser();
        allLayersAddedFromUser.push(...configs);

        saveLayersToLocalStorage(
          allLayersAddedFromUser,
          t("alert_success.file_drop"),
          t("add_layer.localstorage_quota_exceeded")
        );
      } catch {
        changeAlertMessage({
          message: t("alert_error.layer_config_parse_error"),
          type: "error",
        });
      }
      changeExpandAddLayersList(false);
      changeMaximizeTable(false);
    };
  };

  //handle back button
  const handleBackButton = () => {
    changeActiveAddLayersListItem("");
  };

  //push layer to localstorage
  const addLayerFromUser = (layer: WebServiceLayerType) => {
    const allLayersAddedFromUser = getLayersAddedByUser();
    allLayersAddedFromUser.push(layer);
    saveLayersToLocalStorage(allLayersAddedFromUser);
  };

  //add new memory layer
  const addNewMemoryLayer = (layeroptions: mm.IMemoryLayerOptions) => {
    if (!viewer) return;

    const added_layer = viewer
      .getScene()
      .getLayerHandler()
      .addLayer(
        mm.LAYERTYPE.MEMORYLAYER,
        JSON.parse(JSON.stringify(layeroptions))
      ); // Deepcopy, as otherwise the style is overwritten.
    layeroptions.id = added_layer.id;
    addLayerFromUser({
      layers: [layeroptions],
      name: layeroptions.layername,
      type: "memory",
    });
  };

  //prepare layer for saving changes
  const prepareLayer = (layeroptions: mm.ILayerOptions): mm.ILayerOptions => {
    if (!("addedFromUser" in layeroptions)) {
      layeroptions.addedFromUser = true;
    }
    if (!("expanded" in layeroptions)) {
      layeroptions.expanded = true;
    }
    if (!("selectable" in layeroptions)) {
      layeroptions.selectable = true;
    }
    if (!("visible" in layeroptions)) {
      layeroptions.visible = true;
    }
    if (!("editable" in layeroptions)) {
      layeroptions.editable = true;
    }
    if (!("id" in layeroptions)) {
      layeroptions.id = uuidv4();
    }

    return layeroptions;
  };

  //alert user for saving layers changes
  const alertSaveFeature = () => {
    changeAlertMessage({
      message: t("edit_feature.save_message"),
      type: "success",
    });
  };

  //save layer changes
  const handleSaveChanges = () => {
    // If a new memory layer is created
    if (
      activeAddLayersListItem === layersTreeList[0].id &&
      unsavedLayerChanges
    ) {
      const newLayerProperties: mm.IMemoryLayerOptions = {
        layername: unsavedLayerChanges.title,
        title: unsavedLayerChanges.title,
        topic: unsavedLayerChanges.topic,
        style: unsavedLayerChanges.style,
        geometrytype: unsavedLayerChanges.geometrytype,
        propertydef: unsavedLayerChanges.propertydef?.applied,
        editable: true,
      };

      addNewMemoryLayer(prepareLayer(newLayerProperties));
      alertSaveFeature();
    } else if (activeAddLayersListItem === layersTreeList[1].id) {
      const list = newLayerGroupList.filter((item: string) => item !== "");
      list.forEach((item: string) => {
        if (!layerGroups.includes(item)) {
          layerGroups.push(item);
        }
      });
      const allGroups = layerGroups;
      changeLayerGroups([...allGroups]);
      alertSaveFeature();
    } else if (activeAddLayersListItem === layersTreeList[2].id) {
      if (viewer && webServiceLayer.layers) {
        webServiceLayer.layers.map((layer: mm.ILayerOptions) => {
          return JSON.parse(JSON.stringify(prepareLayer(layer)));
        });
        addLayers(viewer, [webServiceLayer], (loaded_sources) => {
          addLayerFromUser(loaded_sources[0]);
        });
      }
      alertSaveFeature();
    }
    setUnsavedLayerChanges(emptyLayerChanges);
    changeNewLayerGroupList([""]);
    changeActiveAddLayersListItem("");
    changeWebServiceLayer({});
    changeWebServiceLayerStep(0);
    changeExpandAddLayersList(false);
    changeMaximizeTable(false);
  };

  //add new webservice layer
  const addWebServiceLayer = (layer: mm.ILayerOptions[]) => {
    layer.forEach((item) => {
      item.addedFromUser = true;
    });
    webServiceLayer.layers = layer;
    changeWebServiceLayersList(layer);
    changeWebServiceLayerStep(webServiceLayerStep + 1);
  };

  //change step position
  const handleNextStep = () => {
    if (webServiceLayerStep === 0) {
      webServiceLayer.addedFromUser = true;
      // Receive all available layers
      if (webServiceLayer.type === "wfs20" && webServiceLayer.url) {
        mm.readWFSGetCapabilities(
          webServiceLayer.url,
          "2.0.0",
          webServiceLayer.source_options
        )
          .then((layers) => {
            changeWebServiceLayersList(layers);
            changeWebServiceLayerStep(webServiceLayerStep + 1);
          })
          .catch((error) => {
            changeAlertMessage({
              message: error.message,
              type: "error",
            });
          });
      } else if (webServiceLayer.type === "qgiswfs" && webServiceLayer.url) {
        const wfsqgis_api = new mm.WFSQGIS(
          webServiceLayer.url,
          webServiceLayer?.project ? webServiceLayer.project : "",
          webServiceLayer.source_options
        );
        setApi(wfsqgis_api);
        wfsqgis_api.readLayerOptionsFromCapabilities(
          (layers) => {
            changeWebServiceLayersList(layers);
            changeWebServiceLayerStep(webServiceLayerStep + 1);
          },
          (error) => {
            changeAlertMessage({
              message: error.message,
              type: "error",
            });
          }
        );
      } else if (webServiceLayer.type === "wfs" && webServiceLayer.url) {
        const wfsgeoserver_api = new mm.WFSGeoserver(
          webServiceLayer.url,
          webServiceLayer?.project ? webServiceLayer.project : "",
          webServiceLayer.source_options
        );
        setApi(wfsgeoserver_api);
        wfsgeoserver_api.describeFeatureTypes(
          (json: any) => {
            const layers_dft = json.featureTypes;
            let layers = layers_dft.map((x: any) => {
              return mm.WFSGeoserverLayer.optionsFromDescribeFeatureType(x);
            });
            layers = layers.map((x: any) => {
              x["title"] = x.layername;
              return x;
            });
            changeWebServiceLayersList(layers);
            changeWebServiceLayerStep(webServiceLayerStep + 1);
            return {};
          },
          (error) => {
            changeAlertMessage({
              message: error.message,
              type: "error",
            });
            return {};
          }
        );
      } else if (webServiceLayer.type === "arcgis" && webServiceLayer.url) {
        const arcgis_api = new mm.ArcgisFeatureServer(
          webServiceLayer.url,
          webServiceLayer?.project || "",
          webServiceLayer.source_options
        );
        setApi(arcgis_api);
        arcgis_api.readfeatureservice(
          function (pjson) {
            const layers = pjson.layers.map((x: any) => {
              return {
                layername: x.id.toString(),
                title: x.name,
              };
            });
            changeWebServiceLayersList(layers);
            changeWebServiceLayerStep(webServiceLayerStep + 1);
          },
          (error) => {
            changeAlertMessage({
              message: error.message,
              type: "error",
            });
          }
        );
      }
      //check for connection name updated or not
      const layer = layerConfiguration.find(
        (item: any) =>
          item.url === webServiceLayer.url && item.type === webServiceLayer.type
      );
      if (layer) {
        const allLayers: mm.Layer[] = [];
        layerConfiguration.forEach((item: any) => {
          if (
            item.url === webServiceLayer.url &&
            item.type === webServiceLayer.type
          ) {
            allLayers.push({ ...item, name: webServiceLayer.name });
          } else {
            allLayers.push(item);
          }
        });
        changeLayerConfiguration(allLayers);
      }
    } else if (webServiceLayerStep === 1 && webServiceLayer.layers) {
      const layersList: any[] = [];
      // Load information for layers to be loaded
      webServiceLayer.layers.forEach((layer: any) => {
        let layeroptions = layer;

        const sourceconfig_defaults = {
          visible:
            typeof webServiceLayer.visible === "undefined"
              ? true
              : webServiceLayer.visible,
          expanded:
            typeof webServiceLayer.expanded === "undefined"
              ? true
              : webServiceLayer.expanded,
        };
        switch (webServiceLayer.type) {
          case "wfs":
            if (!api || !(api instanceof mm.WFSGeoserver)) return;
            api.describeFeatureTypes(
              (json) => {
                const layer_dft = json.featureTypes.filter((x: any) => {
                  return x.typeName == layer.layername;
                })[0];
                if (!api || !(api instanceof mm.WFSGeoserver)) return {};
                api.getStyle(
                  layer_dft.typeName,
                  (style) => {
                    const styles = mm.WFSGeoserverLayer.parseStyle(style);
                    layeroptions =
                      mm.WFSGeoserverLayer.optionsFromDescribeFeatureType(
                        layer_dft,
                        {
                          ...sourceconfig_defaults,
                          ...layer,
                          style: styles.layerstyle,
                          highlightstyle: styles.highlightstyle,
                        }
                      );
                    if (typeof layeroptions.geometrytype === "string") {
                      layeroptions.geometrytype = mm.Geometry.toNumber(
                        layeroptions.geometrytype
                      );
                    }
                    layersList.push(layeroptions);
                    if (
                      webServiceLayer.layers &&
                      webServiceLayer.layers.length === layersList.length
                    ) {
                      addWebServiceLayer(layersList);
                    }
                  },
                  (err) => {
                    changeAlertMessage({
                      message: err.message,
                      type: "error",
                    });
                  }
                );
                return {};
              },
              (err) => {
                changeAlertMessage({
                  message: err.message,
                  type: "error",
                });
                return {};
              }
            );
            break;
          case "arcgis":
            if (!api || !(api instanceof mm.ArcgisFeatureServer)) return;
            api.readlayer(
              layer.layername,
              (pjson) => {
                layeroptions = mm.ArcgisLayer.optionsFromLayerDescription(
                  pjson,
                  { ...sourceconfig_defaults, ...layer }
                );
                if (typeof layeroptions.geometrytype === "string") {
                  layeroptions.geometrytype = mm.Geometry.toNumber(
                    layeroptions.geometrytype
                  );
                }
                layersList.push(layeroptions);
                if (
                  webServiceLayer.layers &&
                  webServiceLayer.layers.length === layersList.length
                ) {
                  addWebServiceLayer(layersList);
                }
              },
              (err) => {
                changeAlertMessage({
                  message: err.message,
                  type: "error",
                });
              }
            );
            break;
          case "qgiswfs":
            if (!api || !(api instanceof mm.WFSQGIS)) return;
            api.readLayerPropertyDefs(
              layer.layername,
              (layerpropertydefs) => {
                if (!api || !(api instanceof mm.WFSQGIS)) return;
                api.readFormatsFromCapabilities(
                  (gmlformat, geojsonformat) => {
                    if (!api || !(api instanceof mm.WFSQGIS)) return;
                    api.gmlformat = gmlformat;
                    api.geojsonformat = geojsonformat;
                    api.readGeometryTypeFromFirstFeature(
                      layer.layername,
                      (geometrytype) => {
                        let enum_geomtype: mm.GEOMETRYTYPE =
                          mm.GEOMETRYTYPE.UNDEFINED;
                        if (typeof geometrytype === "string") {
                          enum_geomtype = mm.Geometry.toNumber(geometrytype);
                        }
                        layeroptions = {
                          ...layer,
                          propertydef: layerpropertydefs,
                          geometrytype: enum_geomtype,
                        };
                        layersList.push(layeroptions);
                        if (
                          webServiceLayer.layers &&
                          webServiceLayer.layers.length === layersList.length
                        ) {
                          addWebServiceLayer(layersList);
                        }
                      },
                      (error) => {
                        changeAlertMessage({
                          message: error.message,
                          type: "error",
                        });
                      }
                    );
                  },
                  (error) => {
                    changeAlertMessage({
                      message: error.message,
                      type: "error",
                    });
                  }
                );
              },
              (error) => {
                changeAlertMessage({
                  message: error.message,
                  type: "error",
                });
              }
            );
            break;

          case "wfs20":
            let fetch_options: any = null;
            if (webServiceLayer.source_options) {
              if (webServiceLayer.source_options.fetch_options) {
                fetch_options = webServiceLayer.source_options.fetch_options;
              }
            }
            mm.readWFSGetCapabilities(
              webServiceLayer.url ? webServiceLayer.url : "",
              "2.0.0",
              fetch_options
            )
              .then((layers_cpb) => {
                const layers_cpb_filtered = layers_cpb.filter((layer_cpb) => {
                  return layer_cpb.layername == layer.layername;
                });
                if (layers_cpb_filtered.length > 0) {
                  const layer_cpb = layers_cpb_filtered[0];
                  const wfs_format =
                    layer_cpb.format ||
                    layer.format ||
                    "application/gml+xml; version=3.2";
                  mm.readWFS20LayerOptions(
                    webServiceLayer.url ? webServiceLayer.url : "",
                    layer_cpb.layername,
                    wfs_format,
                    layer_cpb.version,
                    fetch_options
                  )
                    .then((layer_options) => {
                      layeroptions = {
                        ...layer_options,
                        ...layer_cpb,
                        ...layer,
                      };
                      if (typeof layeroptions.geometrytype === "string") {
                        layeroptions.geometrytype = mm.Geometry.toNumber(
                          layeroptions.geometrytype
                        );
                      }
                      layersList.push(layeroptions);
                      if (
                        webServiceLayer.layers &&
                        webServiceLayer.layers.length === layersList.length
                      ) {
                        addWebServiceLayer(layersList);
                      }
                    })
                    .catch((err) => {
                      changeAlertMessage({
                        message: err,
                        type: "error",
                      });
                    });
                }
              })
              .catch((err) => {
                changeAlertMessage({
                  message: err,
                  type: "error",
                });
              });
            break;
        }
      });
    } else {
      changeWebServiceLayerStep(webServiceLayerStep + 1);
    }
  };

  //discard changes
  const handleDiscardChanges = () => {
    changeAlertMessage({
      message: t("edit_feature.discarded_changes"),
      type: "info",
    });
    setUnsavedLayerChanges(emptyLayerChanges);
    changeNewLayerGroupList([""]);
    changeActiveAddLayersListItem("");
    changeWebServiceLayer({});
    changeWebServiceLayerStep(0);
  };

  //change save button state
  useEffect(() => {
    changeSaveButtonStateOnAddNewLayers(false);
  }, [activeAddLayersListItem]);

  const layersTreeList: LayerContentList[] = [
    {
      id: "create_new_layer",
      label: t("manage_layers.create_title_1"),
      layername: t("manage_layers.create_title_1"),
      description: t("manage_layers.create_description_1"),
      type: "component",
      component: (
        <CreateLayer
          unsavedLayerChanges={unsavedLayerChanges}
          setUnsavedLayerChanges={setUnsavedLayerChanges}
        />
      ),
    },
    {
      id: "create_new_group",
      label: t("manage_layers.create_title_2"),
      layername: t("manage_layers.create_title_2"),
      description: t("manage_layers.create_description_2"),
      type: "component",
      component: <CreateGroup />,
    },
    {
      id: "add_web_service",
      label: t("manage_layers.create_title_3"),
      layername: t("manage_layers.create_title_3"),
      description: t("manage_layers.create_description_3"),
      type: "component",
      component:
        webServiceLayerStep === 0 ? (
          <AddWebServiceLayer />
        ) : webServiceLayerStep === 1 ? (
          <SelectWebServiceLayers />
        ) : (
          <EditWebServiceLayers />
        ),
    },
    // {
    //   id: "reset_layers",
    //   label: t("manage_layers.reset_title"),
    //   layername: t("manage_layers.reset_title"),
    //   description: t("manage_layers.reset_description"),
    //   type: "button",
    //   event: "reset",
    // },
    {
      id: "download_layers",
      label: t("manage_layers.geodata_title_1"),
      layername: t("manage_layers.geodata_title_1"),
      description: t("manage_layers.geodata_description_1"),
      type: "button",
      event: "download",
    },
    {
      id: "upload_layers",
      label: t("manage_layers.geodata_title_2"),
      layername: t("manage_layers.geodata_title_2"),
      description: t("manage_layers.geodata_description_2"),
      type: "button",
      event: "upload",
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        sx={{
          flexDirection: "column",
          gap: 3,
          display: activeAddLayersListItem === "" ? "flex" : "none",
          paddingX: 1,
          paddingBottom: 1,
        }}
      >
        <DropLayer />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, 1fr);",
              md: "repeat(2, 1fr);",
              lg: "repeat(3, 1fr);",
            },
            gap: 3,
          }}
        >
          {layersTreeList.map((item: LayerContentList) => (
            <Box
              key={item.label}
              onClick={() => {
                if (item.type === "button" && item.event) {
                  handleEvent(item.event);
                } else {
                  handleLayer(item.id);
                }
              }}
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: 1.5,
                borderRadius: 1,
                boxShadow: 2,
                gap: 1,
                minHeight: { md: "160px" },
                color: darkTheme ? "white" : "darkblue.main",

                "@media (hover: hover)": {
                  ":hover": { cursor: "pointer", color: "secondary.main" },
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 500,
                  lineHeight: "32px",
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 400,
                  lineHeight: "24px",
                }}
              >
                {item.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: activeAddLayersListItem === "" ? "none" : "block",
        }}
      >
        {layersTreeList
          .filter((item) => item.id === activeAddLayersListItem)
          .map((item: LayerContentList) => (
            <Box
              key={item.label}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 20,
                  color: darkTheme ? "white" : "darkblue.main",
                }}
              >
                {item.label}
              </Typography>
              {item.component}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  marginTop: "auto",
                  bottom: 2,
                }}
              >
                <Box
                  sx={{
                    color: darkTheme ? "white" : "darkblue.main",
                    display: "flex",
                    alignItems: "center",
                    "@media (hover: hover)": {
                      ":hover": { cursor: "pointer", color: "secondary.main" },
                    },
                  }}
                  onClick={() =>
                    monitor(
                      apm,
                      "create-new-layer-back | Add",
                      handleBackButton
                    )
                  }
                >
                  <ChevronLeft fontSize="small" />
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 500,
                      textTransform: "uppercase",
                    }}
                  >
                    {t("manage_layers.button_back")}
                  </Typography>
                </Box>
                <Button
                  onClick={() =>
                    monitor(
                      apm,
                      "create-new-layer-discard | Add",
                      handleDiscardChanges
                    )
                  }
                  sx={{
                    color: darkTheme ? "white" : "darkblue.main",
                    marginLeft: "auto",
                  }}
                  variant="text"
                >
                  {t("manage_layers.button_cancel")}
                </Button>
                <Button
                  onClick={
                    activeAddLayersListItem === "add_web_service" &&
                    webServiceLayerStep !== 2
                      ? handleNextStep
                      : handleSaveChanges
                  }
                  disabled={!saveButtonStateOnAddNewLayers}
                  sx={{
                    backgroundColor: darkTheme ? "white" : "darkblue.main",
                  }}
                  variant="contained"
                >
                  {activeAddLayersListItem === "add_web_service"
                    ? webServiceLayerStep === 0
                      ? t("add_layer.button_connect")
                      : webServiceLayerStep === 1
                        ? t("add_layer.button_add")
                        : t("add_layer.button_save")
                    : t("manage_layers.button_save")}
                </Button>
              </Box>
            </Box>
          ))}
      </Box>
    </Box>
  );
}
